/**
 * BedrockService
 *
 * This service handles direct interactions with LLMs using Vercel AI SDK.
 * Features:
 * - Response caching for faster responses
 * - Structured output support with schema validation
 * - Error handling with consistent response format
 * - Performance optimizations
 * - Cognito-based authentication for improved security
 *
 * @module bedrock.service
 */

import { logger } from '@/lib/utils/logger';
import { generateText, generateObject, streamText } from 'ai';
import { createAmazonBedrock } from '@ai-sdk/amazon-bedrock';
import { z } from 'zod';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import {
  STRUCTURED_OUTPUT_PROMPT,
  TEXT_OUTPUT_PROMPT,
} from '@/lib/prompts/bedrock-prompts';

// Initialize Amazon Bedrock client with credentials
const DEFAULT_MODEL_ID =
  process.env.BEDROCK_MODEL_NAME ||
  'us.anthropic.claude-3-5-sonnet-20241022-v2:0';

// Identity Pool ID for Cognito
const COGNITO_IDENTITY_POOL_ID = process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID || 'us-east-1:004ab70c-e01b-4340-8deb-9169a13cc39c';
const AWS_REGION = process.env.NEXT_PUBLIC_AWS_REGION || 'us-east-1';

export interface BedrockResponse {
  id: string;
  question: string;
  advice: string;
  status: string;
  reasoning?: string;
  urls?: string[];
  page_source?: string[];
  page_title?: string[];
  urls_ccq?: string[];
  page_source_ccq?: string[];
  page_title_ccq?: string[];
  texts_ccq?: string[];
  modifications_ccq?: boolean[];
  urls_ccq_modif?: string[];
  page_source_ccq_modif?: string[];
  page_title_ccq_modif?: string[];
  texts_ccq_modif?: string[];
  caselaw_reference?: string[];
  caselaw_titre?: string[];
  caselaw_lienURL?: string[];
  caselaw_nomsDesPartis?: string[];
  caselaw_texte?: string[];
}

export interface BedrockOptions {
  modelId?: string;
  temperature?: number;
  maxTokens?: number;
  useStructuredOutput?: boolean;
  customSchema?: string | object;
  requestTimeout?: number;
  apiKey?: string;
  dynamicSystemPrompt?: string;
}

/**
 * BedrockService class for handling direct interactions with LLMs using Vercel AI SDK
 */
export class BedrockService {
  private static instance: BedrockService;
  private static initialized = false;
  private static bedrockClient: any = null;
  // Promise to track client initialization state
  private static clientInitializationPromise: Promise<any> | null = null;
  
  private promptCache = new Map<
    string,
    { response: BedrockResponse; timestamp: number }
  >();
  private readonly PROMPT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Default options
  private defaultOptions: BedrockOptions = {
    modelId: DEFAULT_MODEL_ID,
    temperature: 0.0,
    maxTokens: 4096,
    useStructuredOutput: true,
    requestTimeout: 60000,
  };

  /**
   * Initialize the Bedrock client with Cognito credentials or API keys.
   * This method is now idempotent and handles concurrent calls.
   */
  public static initializeClient(cognitoToken?: string): Promise<any> {
    // If initialization is already in progress or completed, return the existing promise/client
    if (BedrockService.clientInitializationPromise) {
      return BedrockService.clientInitializationPromise;
    }

    // Start initialization
    BedrockService.clientInitializationPromise = (async () => {
      try {
        // If token is provided, use it for getting credentials
        if (cognitoToken) {
          logger.info('[BedrockService] Initializing with Cognito token', {
            tokenLength: cognitoToken.length,
            tokenPrefix: cognitoToken.substring(0, 10) + '...',
            identityPoolId: COGNITO_IDENTITY_POOL_ID,
            userPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID,
          });

          const loginKey = `cognito-idp.${AWS_REGION}.amazonaws.com/${process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID}`;
          logger.debug('[BedrockService] Cognito login configuration', {
            loginKey,
            region: AWS_REGION,
          });

          const credentials = fromCognitoIdentityPool({
            clientConfig: { region: AWS_REGION },
            identityPoolId: COGNITO_IDENTITY_POOL_ID,
            logins: {
              [loginKey]: cognitoToken,
            },
          });

          try {
            const credentialsObj = await credentials();
            logger.info('[BedrockService] Successfully obtained AWS credentials from Cognito', {
              hasAccessKey: !!credentialsObj.accessKeyId,
              hasSecretKey: !!credentialsObj.secretAccessKey,
              hasSessionToken: !!credentialsObj.sessionToken,
            });

            BedrockService.bedrockClient = createAmazonBedrock({
              region: AWS_REGION,
              accessKeyId: credentialsObj.accessKeyId,
              secretAccessKey: credentialsObj.secretAccessKey,
              sessionToken: credentialsObj.sessionToken,
            });

            logger.info('[BedrockService] Client initialized with Cognito credentials.');
            return BedrockService.bedrockClient;
          } catch (credentialError) {
            logger.error('[BedrockService] Error obtaining credentials from Cognito', {
              error: (credentialError as Error).message,
              stack: (credentialError as Error).stack,
            });
            // Reset promise on failure to allow retry
            BedrockService.clientInitializationPromise = null;
            throw credentialError;
          }
        } else if (process.env.AWS_ACCESS_KEY_ID && process.env.AWS_SECRET_ACCESS_KEY) {
          // Use environment variables as fallback (server-side)
          logger.info('[BedrockService] Initializing with API keys (server-side)');
          BedrockService.bedrockClient = createAmazonBedrock({
            region: AWS_REGION,
            accessKeyId: process.env.AWS_ACCESS_KEY_ID,
            secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY,
          });
          logger.info('[BedrockService] Client initialized with API keys.');
          return BedrockService.bedrockClient;
        } else {
          // No valid authentication method found
          const error = new Error('No authentication method available for Bedrock');
          logger.error('[BedrockService] Initialization failed', { error: error.message });
          BedrockService.clientInitializationPromise = null; // Reset promise
          throw error;
        }
      } catch (error) {
        logger.error('[BedrockService] Critical error during client initialization:', {
          error: (error as Error).message,
          stack: (error as Error).stack,
        });
        BedrockService.clientInitializationPromise = null; // Reset promise on any error
        throw error; // Re-throw the error after logging
      }
    })(); // Immediately invoke the async function

    return BedrockService.clientInitializationPromise;
  }

  /**
   * Get singleton instance of BedrockService
   */
  public static getInstance(): BedrockService {
    if (!BedrockService.instance) {
      BedrockService.instance = new BedrockService();

      // Log initialization message only once per instance creation
      if (!BedrockService.initialized) {
        logger.info(
          '[BedrockService] Singleton instance created', {
            modelId: DEFAULT_MODEL_ID,
            temperature: 0.0,
            maxTokens: 4096,
            useStructuredOutput: true,
            requestTimeout: 60000,
          }
        );
        BedrockService.initialized = true;
      }
    }
    return BedrockService.instance;
  }

  /**
   * Private constructor - use getInstance() instead
   */
  private constructor() {
    // Initialization logic is handled by initializeClient
  }

  /**
   * Process and validate response format
   */
  private processResponse(
    response: unknown,
    originalPrompt: string
  ): BedrockResponse {
    // For markdown output, just return as advice field
    if (typeof response === 'string') {
      return {
        id: `response-${Date.now()}`,
        question: originalPrompt,
        advice: response,
        status: 'completed',
        reasoning: '',
      };
    }

    if (typeof response === 'object' && response !== null) {
      // TypeScript type guard to ensure response has required fields
      const res = response as Record<string, any>;

      if (res.id && typeof res.advice === 'string') {
        return {
          id: res.id,
          question: res.question || originalPrompt,
          advice: res.advice,
          status: res.status || 'completed',
          reasoning: res.reasoning || '',
          urls: Array.isArray(res.urls) ? res.urls : undefined,
          page_source: Array.isArray(res.page_source)
            ? res.page_source
            : undefined,
          page_title: Array.isArray(res.page_title)
            ? res.page_title
            : undefined,
          urls_ccq: Array.isArray(res.urls_ccq) ? res.urls_ccq : undefined,
          page_source_ccq: Array.isArray(res.page_source_ccq)
            ? res.page_source_ccq
            : undefined,
          page_title_ccq: Array.isArray(res.page_title_ccq)
            ? res.page_title_ccq
            : undefined,
          texts_ccq: Array.isArray(res.texts_ccq) ? res.texts_ccq : undefined,
          modifications_ccq: Array.isArray(res.modifications_ccq)
            ? res.modifications_ccq
            : undefined,
          urls_ccq_modif: Array.isArray(res.urls_ccq_modif)
            ? res.urls_ccq_modif
            : undefined,
          page_source_ccq_modif: Array.isArray(res.page_source_ccq_modif)
            ? res.page_source_ccq_modif
            : undefined,
          page_title_ccq_modif: Array.isArray(res.page_title_ccq_modif)
            ? res.page_title_ccq_modif
            : undefined,
          texts_ccq_modif: Array.isArray(res.texts_ccq_modif)
            ? res.texts_ccq_modif
            : undefined,
          caselaw_reference: Array.isArray(res.caselaw_reference)
            ? res.caselaw_reference
            : undefined,
          caselaw_titre: Array.isArray(res.caselaw_titre)
            ? res.caselaw_titre
            : undefined,
          caselaw_lienURL: Array.isArray(res.caselaw_lienURL)
            ? res.caselaw_lienURL
            : undefined,
          caselaw_nomsDesPartis: Array.isArray(res.caselaw_nomsDesPartis)
            ? res.caselaw_nomsDesPartis
            : undefined,
          caselaw_texte: Array.isArray(res.caselaw_texte)
            ? res.caselaw_texte
            : undefined,
        };
      }
    }

    // Fallback for non-object or invalid responses
    return {
      id: `response-${Date.now()}`,
      question: originalPrompt,
      advice:
        typeof response === 'string' ? response : JSON.stringify(response),
      status: 'completed',
      reasoning: '',
    };
  }

  /**
   * Ensure Bedrock client is initialized before making requests
   */
  private async ensureClient(cognitoToken?: string): Promise<any> {
    // Use the robust initializeClient method
    // It handles idempotency and returns the promise
    try {
      const client = await BedrockService.initializeClient(cognitoToken);
      if (!client) {
        throw new Error('Bedrock client initialization returned null or undefined.');
      }
      return client;
    } catch (error) {
      logger.error('[BedrockService] Failed to ensure client is initialized.', {
         error: (error as Error).message,
         stack: (error as Error).stack
      });
      // Re-throw the error to prevent proceeding with an uninitialized client
      throw new Error(`Failed to ensure Bedrock client initialization: ${(error as Error).message}`);
    }
  }

  /**
   * Call LLM API to get a response for a prompt using the Vercel AI SDK
   */
  public async getBedrockResponse(
    prompt: string,
    options: BedrockOptions = {}
  ): Promise<BedrockResponse> {
    // Merge options with defaults
    const mergedOptions = { ...this.defaultOptions, ...options };
    const {
      modelId = this.defaultOptions.modelId,
      temperature = this.defaultOptions.temperature,
      maxTokens = this.defaultOptions.maxTokens,
      useStructuredOutput = this.defaultOptions.useStructuredOutput,
      requestTimeout = this.defaultOptions.requestTimeout,
      dynamicSystemPrompt,
      apiKey, // This would now be the Cognito token if provided
    } = mergedOptions;

    // Generate a cache key for this request
    const cacheKey = JSON.stringify({
      prompt,
      modelId,
      temperature,
      maxTokens,
      useStructuredOutput,
      dynamicSystemPrompt,
      // Don't include apiKey in cache key for security
    });

    // Check prompt cache first
    const cached = this.promptCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.PROMPT_CACHE_TTL) {
      logger.debug('[BedrockService] Using cached prompt response');
      return cached.response;
    }

    try {
      // Ensure client is initialized with credentials (using Cognito token if available)
      await this.ensureClient(apiKey);
      
      // Determine the system prompt to use
      const systemPrompt = dynamicSystemPrompt
        ? dynamicSystemPrompt
        : useStructuredOutput
          ? STRUCTURED_OUTPUT_PROMPT
          : TEXT_OUTPUT_PROMPT;

      // For both structured and non-structured output, we just want markdown text
      const { text } = await generateText({
        model: BedrockService.bedrockClient(modelId || DEFAULT_MODEL_ID),
        prompt,
        temperature: temperature || 0,
        maxTokens: maxTokens,
        system: systemPrompt,
      });

      // For pure markdown response, we don't need to try parsing JSON
      const structuredResponse: BedrockResponse = {
        id: `response-${Date.now()}`,
        question: prompt,
        advice: text,
        status: 'completed',
        reasoning: '', // We don't have separate reasoning in markdown mode
      };

      // Cache the processed response
      this.promptCache.set(cacheKey, {
        response: structuredResponse,
        timestamp: Date.now(),
      });

      return structuredResponse;
    } catch (error) {
      logger.error('[BedrockService] Error calling AI SDK:', {
        error: (error as Error).message,
        stack: (error as Error).stack,
        prompt: prompt.substring(0, 100) + '...',
      });

      // Return error response
      return {
        id: `error-${Date.now()}`,
        question: prompt,
        advice: `Error: ${(error as Error).message}`,
        status: 'error',
        reasoning: '',
      };
    }
  }

  /**
   * Call LLM API to get a streamed response for a prompt using the Vercel AI SDK
   */
  public async streamBedrockResponse(
    prompt: string,
    options: BedrockOptions = {}
  ): Promise<ReadableStream<Uint8Array>> {
    // Merge options with defaults
    const mergedOptions = { ...this.defaultOptions, ...options };
    const {
      modelId = this.defaultOptions.modelId,
      temperature = this.defaultOptions.temperature,
      maxTokens = this.defaultOptions.maxTokens,
      useStructuredOutput = this.defaultOptions.useStructuredOutput,
      requestTimeout = this.defaultOptions.requestTimeout,
      dynamicSystemPrompt,
      apiKey, // This would now be the Cognito token if provided
    } = mergedOptions;

    try {
      logger.info('[BedrockService] Starting streaming response', {
        modelId,
        promptLength: prompt.length,
      });

      // Ensure client is initialized with credentials (using Cognito token if available)
      await this.ensureClient(apiKey);
      
      // Determine the system prompt to use
      const systemPrompt = dynamicSystemPrompt
        ? dynamicSystemPrompt
        : useStructuredOutput
          ? STRUCTURED_OUTPUT_PROMPT
          : TEXT_OUTPUT_PROMPT;

      // Use streamText from Vercel AI SDK
      const stream = await streamText({
        model: BedrockService.bedrockClient(modelId || DEFAULT_MODEL_ID),
        prompt,
        temperature: temperature || 0,
        maxTokens: maxTokens,
        system: systemPrompt,
      });

      // Convert to a ReadableStream<Uint8Array> using the response body
      const response = stream.toTextStreamResponse();
      return response.body as ReadableStream<Uint8Array>;
    } catch (error) {
      logger.error('[BedrockService] Error starting stream:', {
        error: (error as Error).message,
        stack: (error as Error).stack,
      });

      // Create error stream
      const encoder = new TextEncoder();
      return new ReadableStream({
        start(controller) {
          controller.enqueue(
            encoder.encode(
              `data: ${JSON.stringify({
                error: (error as Error).message,
                status: 'error',
              })}\n\n`
            )
          );
          controller.close();
        },
      });
    }
  }
}

// Export singleton instance
export const bedrockService = BedrockService.getInstance();
